import { useState, useCallback } from "react"
import { MRT_ColumnFiltersState } from "material-react-table"
import moment from "moment"

// ─── IDs de colonnes qui contiennent des dates ────────────────────────────────
// Ajoutez ici tous les IDs de colonnes date de vos tableaux
const DATE_FILTER_IDS = [
  "dateOfLastConnexion",
  "Date de livraison",
  "Date de commande",
  "createdAt",
  "delivryDate",
]

/**
 * Restaure les valeurs de filtres depuis le localStorage.
 * Les filtres de date sont reconvertis en objet moment
 * car JSON.parse retourne des strings, pas des objets moment.
 */
const deserializeFilters = (
  filters: MRT_ColumnFiltersState,
): MRT_ColumnFiltersState => {
  return filters.map((filter) => {
    if (DATE_FILTER_IDS.includes(filter.id) && filter.value) {
      const m = moment(filter.value as string)
      return { ...filter, value: m.isValid() ? m : filter.value }
    }
    return filter
  })
}

/**
 * Hook réutilisable pour persister les filtres d'un tableau dans le localStorage.
 *
 * Usage:
 *   const { columnFilters, setColumnFilters, clearFilters } = usePersistedFilters("craftman_list_filters")
 *   const { columnFilters, setColumnFilters, clearFilters } = usePersistedFilters("order_list_filters")
 *
 * Clés recommandées:
 *   - "craftman_list_filters"  → listing artisans
 *   - "order_list_filters"     → listing commandes
 *   - "customer_list_filters"  → listing clients
 *   - "product_list_filters"   → listing produits
 */
export const usePersistedFilters = (storageKey: string) => {
  // Initialise depuis le localStorage au montage — avec désérialisation des dates
  const [columnFilters, setColumnFiltersState] =
    useState<MRT_ColumnFiltersState>(() => {
      try {
        const saved = localStorage.getItem(storageKey)
        if (!saved) return []
        const parsed: MRT_ColumnFiltersState = JSON.parse(saved)
        return deserializeFilters(parsed)
      } catch {
        return []
      }
    })

  // Wrapper qui sauvegarde dans localStorage à chaque changement
  // Les valeurs moment sont sérialisées en ISO string avant stockage
  const setColumnFilters: typeof setColumnFiltersState = useCallback(
    (updater) => {
      setColumnFiltersState((prev) => {
        const next =
          typeof updater === "function" ? updater(prev) : updater
        try {
          const serializable = next.map((filter) => {
            if (
              DATE_FILTER_IDS.includes(filter.id) &&
              moment.isMoment(filter.value)
            ) {
              return {
                ...filter,
                value: (filter.value as moment.Moment).toISOString(),
              }
            }
            return filter
          })
          localStorage.setItem(storageKey, JSON.stringify(serializable))
        } catch {
          // Silently fail si localStorage indisponible ou quota dépassé
        }
        return next
      })
    },
    [storageKey],
  )

  // Réinitialise les filtres et nettoie le localStorage
  const clearFilters = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // Silently fail
    }
    setColumnFiltersState([])
  }, [storageKey])

  return { columnFilters, setColumnFilters, clearFilters }
}